/* eslint-disable import/no-cycle */
import { FORM_COMPONENT_BASIC_INPUT_TYPE } from '../schemas/formComponentInputTypeSchema';
import { getFormSchemaByInputType } from '../schemas/formFieldSchema';
import { ComponentInputTypeT } from '../schemas/types';
import { FormAddItemPayload, FormBuilderState } from '../store/formBuilderModel';
import { FormItemT } from '../types';
import {
  isFieldSchema,
  findItemByPath,
  isSectionSchema,
  addItemByPath,
  populateHierarchyInfo,
  removeFollowupOptionPath,
  createAdvancedField,
  createNewField,
  createNewSection,
} from '../utils';

export function addToCanvas(state: FormBuilderState, action: FormAddItemPayload) {
  const { draggedItem, dropTarget } = action;

  if (!draggedItem || draggedItem.type !== 'paletteItem') {
    return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }
  const { path } = dropTarget;

  const itemType = draggedItem.itemData as ComponentInputTypeT;
  const droppedItemProperties = getFormSchemaByInputType(itemType);

  let newItem: FormItemT;

  switch (droppedItemProperties.panel) {
    case 'page-element':
      newItem = createNewSection(state.formSchema, 'Section');
      break;
    case 'advanced':
      newItem = createAdvancedField(state.formSchema, itemType);
      break;
    case 'basic':
      if (!dropTarget.parentId) {
        newItem = createNewSection(state.formSchema, 'Section');
        (newItem as FormItemT & { fields?: FormItemT[] }).fields = [createNewField(state.formSchema, droppedItemProperties.type)];
      } else {
        newItem = createNewField(state.formSchema, droppedItemProperties.type);
      }
      break;
    default:
      return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  const parentPath = path.slice(0, -1);
  const insertionIndex = path[path.length - 1];

  const updatedSchema = addItemByPath(state.formSchema, parentPath, insertionIndex, newItem);
  const newSchemaWithHierarchy = populateHierarchyInfo(updatedSchema, null, null, null);

  return {
    ...state,
    formSchema: newSchemaWithHierarchy,
    draggedItem: null,
    dropIndicator: undefined,
    scrollToItemId: newItem.id,
  };
}

export function addToSection(state: FormBuilderState, action: FormAddItemPayload) {
  const { draggedItem, dropTarget } = action;

  if (!draggedItem || draggedItem.type !== 'paletteItem') {
    return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  const itemType = draggedItem.itemData as ComponentInputTypeT;
  const droppedItemProperties = getFormSchemaByInputType(itemType);

  const parentPath = dropTarget.path.slice(0, -1);
  const parent = findItemByPath(state.formSchema, parentPath);

  if (!parent || !isSectionSchema(parent) || droppedItemProperties.panel === 'page-element' || parent.isAdvanced) {
    return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  let newItem: FormItemT;
  switch (droppedItemProperties.panel) {
    case 'basic':
      newItem = createNewField(state.formSchema, itemType);
      break;
    case 'advanced':
      newItem = createAdvancedField(state.formSchema, itemType);
      break;
    default:
      return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  const insertionIndex = dropTarget.path[dropTarget.path.length - 1];

  const updatedSchema = addItemByPath(state.formSchema, parentPath, insertionIndex, newItem);
  const newSchemaWithHierarchy = populateHierarchyInfo(updatedSchema, null, null, null);

  return {
    ...state,
    formSchema: newSchemaWithHierarchy,
    draggedItem: null,
    dropIndicator: undefined,
    scrollToItemId: newItem.id,
  };
}

export function addToFollowup(state: FormBuilderState, action: FormAddItemPayload) {
  const { draggedItem, dropTarget, optionId } = action;
  const { followupPath: yesNoQuestionPath, path } = dropTarget;

  const fullInsertionPath = removeFollowupOptionPath(yesNoQuestionPath ?? [], path);
  if (!draggedItem || draggedItem.type !== 'paletteItem' || !yesNoQuestionPath || !optionId || !fullInsertionPath) {
    return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  const itemType = draggedItem.itemData as ComponentInputTypeT;
  const draggedItemProperties = getFormSchemaByInputType(itemType);

  if (draggedItemProperties.panel === 'page-element') {
    return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  const yesNoQuestionItem = findItemByPath(state.formSchema, yesNoQuestionPath);
  if (
    !yesNoQuestionItem ||
    !isFieldSchema(yesNoQuestionItem) ||
    yesNoQuestionItem.inputType !== FORM_COMPONENT_BASIC_INPUT_TYPE.yesNo
  ) {
    return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  let newItem: FormItemT;
  switch (draggedItemProperties.panel) {
    case 'basic':
      newItem = createNewField(state.formSchema, itemType);
      newItem.optionId = optionId;
      newItem.isFollowUp = true;
      break;
    case 'advanced':
      newItem = createAdvancedField(state.formSchema, itemType);
      newItem.optionId = optionId;

      if (isSectionSchema(newItem) && newItem.fields) {
        newItem.fields = newItem.fields.map((field) => {
          return { ...field, parentSection: newItem.section, isFollowUp: true, optionId };
        });
      }
      break;
    default:
      return { ...state, draggedItem: null, dropIndicator: undefined, scrollToItemId: null };
  }

  const insertionDirectParentPath = fullInsertionPath.slice(0, -1);
  const insertionIndex = fullInsertionPath[fullInsertionPath.length - 1];

  const updatedSchema = addItemByPath(state.formSchema, insertionDirectParentPath, insertionIndex, newItem);
  const newSchemaWithHierarchy = populateHierarchyInfo(updatedSchema, null, null, null);

  return {
    ...state,
    formSchema: newSchemaWithHierarchy,
    draggedItem: null,
    dropIndicator: undefined,
    scrollToItemId: newItem.id,
  };
}
